import { useMemo } from 'react';

import { AccountFee, BillingGroupRun, PaymentStatus, RunStatus } from '@ndvr/billing-api';

import { parseAmount } from './billing-run-table';

const allAmountsKnown = (amounts: (string | undefined)[]): amounts is string[] =>
    amounts.every((amount) => amount != null);
const sumAmounts = (amounts: string[]) => amounts.reduce((total, amount) => total + parseAmount(amount), 0);
const sumOptionalAmounts = (amounts: (string | undefined)[]) => {
    const knownAmounts = amounts.filter((amount): amount is string => amount != null);
    return knownAmounts.length === 0 ? undefined : sumAmounts(knownAmounts);
};
const countPositiveAmounts = (amounts: (string | undefined)[]) =>
    allAmountsKnown(amounts) ? amounts.filter((amount) => parseAmount(amount) > 0).length : undefined;

// Collected fees only exist per fee detail; the service does not provide this rollup.
const settledAmountOf = (fee: AccountFee) => sumAmounts(fee.feeDetails.map((detail) => detail.settledAmount));
const settledAmountOfRun = (run: BillingGroupRun) =>
    run.accountFees.reduce((total, fee) => total + settledAmountOf(fee), 0);

export const useBillingRunSummary = (runs: BillingGroupRun[]) =>
    useMemo(() => {
        const accountFees = runs.flatMap((run) => run.accountFees);
        const totalCalculatedFee = sumAmounts(runs.map((run) => run.chargedAmount));
        const totalBillableNav = sumAmounts(runs.map((run) => run.billableAum));
        const cashEncumbered = sumOptionalAmounts(runs.map((run) => run.cashEncumbered));
        const outstandingFees = sumAmounts(runs.map((run) => run.totalOutstandingFee));
        const shortToCover = sumOptionalAmounts(runs.map((run) => run.cashShortfall));
        const accountShortfalls = accountFees.map((fee) => fee.cashShortfall);
        const groupShortfalls = runs.map((run) => run.cashShortfall);
        const shortAccounts = countPositiveAmounts(accountShortfalls);
        const shortGroups = countPositiveAmounts(groupShortfalls);
        const coveredGroups = allAmountsKnown(groupShortfalls)
            ? runs.filter(
                  (run, index) => run.runStatus !== RunStatus.FAILED && parseAmount(groupShortfalls[index]) === 0,
              ).length
            : undefined;
        const settledToDate = runs.reduce((total, run) => total + settledAmountOfRun(run), 0);
        const settledGroups = runs.filter(
            (run) => run.runStatus === RunStatus.COMPLETED && run.paymentStatus === PaymentStatus.PAID,
        ).length;
        const unpaidAccounts = accountFees.filter((fee) => fee.paymentStatus === PaymentStatus.UNPAID).length;
        const partialAccounts = accountFees.filter((fee) => fee.paymentStatus === PaymentStatus.PARTIALLY_PAID).length;

        return {
            cashEncumbered,
            coveredGroups,
            feeCalculatedGroups: runs.filter((run) => run.runStatus !== RunStatus.FAILED).length,
            outstandingFees,
            partialAccounts,
            settledGroups,
            settledToDate,
            shortAccounts,
            shortGroups,
            shortToCover,
            totalBillableNav,
            totalCalculatedFee,
            totalGroups: runs.length,
            unpaidAccounts,
        };
    }, [runs]);
